/**
 * Integrytas Group - Common Security Module (CSM)
 * YesNoBadge - small Acumatica-style pill that renders "Yes" / "No" with
 * subtle color (emerald for Yes, neutral gray for No).
 *
 * Locale-aware per v14 standard 5.5: when no explicit yes/no labels are
 * passed in, the badge resolves the strings from the active i18next
 * resource bundle so Spanish users see "Si/No" instead of "Yes/No".
 *
 * Accepts either a boolean or the DB-side 'Y' / 'N' single-character flag.
 * Callers may still pass `yesLabel` / `noLabel` overrides for semantic
 * labels like "Active / Inactive" — those overrides are themselves
 * expected to be already-translated strings from useTranslation().
 */

import { useTranslation } from 'react-i18next';

export interface YesNoBadgeProps {
  value: boolean | 'Y' | 'N' | null | undefined;
  yesLabel?: string;
  noLabel?: string;
}

export function YesNoBadge({ value, yesLabel, noLabel }: YesNoBadgeProps) {
  const { t } = useTranslation();
  const isYes = value === true || value === 'Y';

  const resolvedYes = yesLabel ?? t('common.yes');
  const resolvedNo  = noLabel  ?? t('common.no');

  const cls = isYes
    ? 'border-csm-success-border bg-csm-success-soft text-csm-success'
    : 'border-csm-border bg-csm-chip text-csm-muted';

  return (
    <span
      className={'inline-block rounded-full border px-2 py-0.5 text-csm-2xs uppercase tracking-wider ' + cls}
    >
      {isYes ? resolvedYes : resolvedNo}
    </span>
  );
}
